import { formatBaht } from '@xver/utils/number';
import { Badge, Checkbox } from '@mantine/core';
import { IconAlertCircle, IconCurrencyBaht, IconWallet } from '@tabler/icons-react';
import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, InputField, NewTabLink } from '@xver/components/Atoms';

import { MONEY_GREEN, MONEY_GREEN_DEEP } from '../methodTheme';

const QUICK_AMOUNTS = [50, 100, 300, 500, 1000, 3000, 5000, 10000];

interface TopupAmountFormProps {
    amount: number;
    onAmountChange: (value: number) => void;
    agreed: boolean;
    onAgreedChange: (value: boolean) => void;
    submitting: boolean;
    onSubmit: () => void | Promise<void>;
    extraHeader?: ReactNode;
    extraError?: string;
    ctaLabel?: string;
    ctaDisabled?: boolean;
}

export default function TopupAmountForm({
    amount,
    onAmountChange,
    agreed,
    onAgreedChange,
    submitting,
    onSubmit,
    extraHeader,
    extraError,
    ctaLabel,
    ctaDisabled,
}: TopupAmountFormProps) {
    const { t } = useTranslation('topup');
    const [touched, setTouched] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const validation = useMemo(() => {
        if (!Number.isFinite(amount)) return t('errors.amountInvalid');
        if (amount < 50 || amount > 100000) return t('errors.amountRange');
        return undefined;
    }, [amount, t]);

    const visibleError = touched || submitAttempted ? validation : undefined;
    const formattedPreview = useMemo(() => {
        if (!Number.isFinite(amount)) return '0';
        return formatBaht(amount, 0, 2);
    }, [amount]);

    const handleSubmit = async () => {
        if (!submitAttempted) setSubmitAttempted(true);
        if (validation || extraError) return;
        await onSubmit();
    };

    const isPayDisabled = !agreed || Boolean(validation) || Boolean(extraError) || ctaDisabled;

    return (
        <div className="w-full p-5 flex flex-col gap-6">
            {extraHeader}

            <div className="flex flex-col gap-2.5">
                <span className="text-sm font-medium text-fg/85">{t('form.quickPick')}</span>
                <div className="w-full grid grid-cols-[repeat(auto-fit,minmax(6rem,1fr))] gap-2">
                    {QUICK_AMOUNTS.map((value) => {
                        const active = amount === value;
                        return (
                            <Badge
                                key={value}
                                component="button"
                                type="button"
                                aria-pressed={active}
                                onClick={() => {
                                    onAmountChange(value);
                                    if (!touched) setTouched(true);
                                }}
                                variant={active ? 'filled' : 'light'}
                                color={active ? 'blue' : 'gray'}
                                size="lg"
                                radius="sm"
                                fullWidth
                                styles={{ root: { cursor: 'pointer', height: '36px' } }}
                            >
                                ฿{value.toLocaleString('en-US')}
                            </Badge>
                        );
                    })}
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <InputField
                    size="md"
                    type="number"
                    min={50}
                    max={100000}
                    label={t('form.amount')}
                    description={t('form.amountHint')}
                    placeholder={t('form.amountPlaceholder')}
                    value={Number.isFinite(amount) ? amount : ''}
                    onChange={(e) => {
                        const next = e.target.value === '' ? NaN : Number(e.target.value);
                        onAmountChange(next);
                        if (!touched) setTouched(true);
                    }}
                    leftIcon={<IconCurrencyBaht size={18} stroke={2.5} className="text-fg/80" />}
                    error={visibleError}
                />
                {!visibleError && (
                    <div className="inline-flex items-center gap-2 text-sm text-fg/75">
                        <span
                            className="inline-block size-1.5 rounded-full"
                            style={{ background: MONEY_GREEN }}
                        />
                        <span>{t('form.preview', { amount: formattedPreview })}</span>
                    </div>
                )}
            </div>

            <div className="rounded-md border border-line bg-surface-1 px-4 py-3">
                <Checkbox
                    checked={agreed}
                    onChange={(e) => onAgreedChange(e.currentTarget.checked)}
                    color="green"
                    label={
                        <span className="text-sm">
                            {t('terms.label')}{' '}
                            <NewTabLink
                                href="https://xver.cloud/th/legal/refund-policy/"
                                className="text-blue-400 light:text-blue-700 hover:underline"
                            >
                                {t('terms.readMore')}
                            </NewTabLink>
                        </span>
                    }
                />
            </div>

            <div className="inline-flex items-center gap-2 text-base text-amber-400/90 light:text-amber-800">
                <IconAlertCircle size={20} className="mt-0.5 shrink-0" />
                <span>{t('form.expirationWarning')}</span>
            </div>

            <div className="w-full flex justify-stretch lg:justify-end">
                <Button
                    isLoading={submitting}
                    disabled={isPayDisabled}
                    onClick={handleSubmit}
                    size="lg"
                    bgColor={MONEY_GREEN}
                    borderColor={MONEY_GREEN}
                    textColor={MONEY_GREEN_DEEP}
                    fontWeight={700}
                    leftIcon={<IconWallet size={18} stroke={2.4} />}
                    className="lg:w-auto! lg:min-w-64!"
                >
                    {ctaLabel ?? t('form.pay')}
                </Button>
            </div>
        </div>
    );
}
